import { Container, Sprite } from 'pixi.js-legacy';
import type { Boss } from '../../entities/Boss';
import { Splitter } from '../../entities/bosses/Splitter';
import { bossTextureCache } from '../utils/BossTextureCache';

/**
 * Boss 오브젝트 풀
 * RenderTexture 캐싱을 사용하여 보스 렌더링 성능 최적화
 */
class BossPool {
    private pool: Sprite[] = [];
    private active: Map<Boss, {
        sprite: Sprite;
        lastHealthLevel: number;
        lastSplitLevel: number;
        bossType: string;
        radius: number;
    }> = new Map();
    private container: Container;
    // iOS: 공격적인 풀 크기 제한
    private maxPoolSize: number = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 5 : 10;

    constructor(container: Container) {
        this.container = container;
    }

    get(boss: Boss): Sprite | null {
        const healthLevel = bossTextureCache.healthRatioToLevel(boss.health / boss.maxHealth);
        const splitLevel = boss instanceof Splitter ? boss.getSplitLevel() : 0;

        // 스프라이트 생성 또는 풀에서 가져오기
        let sprite = this.pool.pop();

        if (!sprite) {
            const newSprite = bossTextureCache.createBossSprite(boss.bossType, healthLevel, boss.radius, splitLevel);
            if (!newSprite) {
                // 캐시 미초기화 시 null 반환 (Graphics 폴백 필요)
                return null;
            }
            sprite = newSprite;
        } else {
            // 풀에서 가져온 스프라이트의 텍스쳐 업데이트
            const texture = boss.bossType === 'splitter'
                ? bossTextureCache.getSplitterTexture(healthLevel, boss.radius, splitLevel)
                : bossTextureCache.getOrCreateTexture(boss.bossType, healthLevel, boss.radius);

            if (texture) {
                sprite.texture = texture;
            }
        }

        sprite.position.set(boss.x, boss.y);
        sprite.rotation = boss.rotation;
        sprite.visible = true;

        this.active.set(boss, {
            sprite,
            lastHealthLevel: healthLevel,
            lastSplitLevel: splitLevel,
            bossType: boss.bossType,
            radius: boss.radius
        });
        this.container.addChild(sprite);

        return sprite;
    }

    release(boss: Boss): void {
        const data = this.active.get(boss);
        if (data) {
            data.sprite.visible = false;
            this.container.removeChild(data.sprite);
            this.active.delete(boss);

            // 풀 크기 제한
            if (this.pool.length < this.maxPoolSize) {
                this.pool.push(data.sprite);
            } else {
                data.sprite.destroy();
            }
        }
    }

    update(boss: Boss): void {
        const data = this.active.get(boss);
        if (!data) return;

        const { sprite, lastHealthLevel, lastSplitLevel, radius } = data;

        // 위치/회전 항상 업데이트
        sprite.position.set(boss.x, boss.y);
        sprite.rotation = boss.rotation;

        // 체력 레벨 변경 체크
        const currentHealthLevel = bossTextureCache.healthRatioToLevel(boss.health / boss.maxHealth);
        const currentSplitLevel = boss instanceof Splitter ? boss.getSplitLevel() : 0;

        // 체력 또는 분할 레벨이 변경된 경우 텍스쳐 업데이트
        if (lastHealthLevel !== currentHealthLevel || lastSplitLevel !== currentSplitLevel) {
            const texture = boss.bossType === 'splitter'
                ? bossTextureCache.getSplitterTexture(currentHealthLevel, radius, currentSplitLevel)
                : bossTextureCache.getOrCreateTexture(boss.bossType, currentHealthLevel, radius);

            if (texture) {
                sprite.texture = texture;
            }

            data.lastHealthLevel = currentHealthLevel;
            data.lastSplitLevel = currentSplitLevel;
        }
    }

    has(boss: Boss): boolean {
        return this.active.has(boss);
    }

    getActiveKeys(): IterableIterator<Boss> {
        return this.active.keys();
    }

    clear(): void {
        for (const [boss, data] of this.active) {
            data.sprite.visible = false;
            this.container.removeChild(data.sprite);

            if (this.pool.length < this.maxPoolSize) {
                this.pool.push(data.sprite);
            } else {
                data.sprite.destroy();
            }
        }
        this.active.clear();
    }

    reset(): void {
        // clearAll()로 이미 컨테이너에서 제거되었으므로 참조만 정리
        this.active.clear();
        // 풀의 모든 스프라이트 destroy
        for (const sprite of this.pool) {
            sprite.destroy();
        }
        this.pool.length = 0;
    }

    destroy(): void {
        this.clear();
        for (const sprite of this.pool) {
            sprite.destroy();
        }
        this.pool.length = 0;
    }
}

/**
 * Boss PixiJS 어댑터 (RenderTexture 캐싱 적용)
 */
export class BossAdapter {
    private bossPool: BossPool;
    private cacheReady: boolean = false;

    constructor(container: Container) {
        this.bossPool = new BossPool(container);
        this.cacheReady = bossTextureCache.isInitialized();
    }

    sync(bosses: Boss[]): void {
        // 캐시 준비 상태 확인
        if (!this.cacheReady) {
            this.cacheReady = bossTextureCache.isInitialized();
        }

        // bosses 배열을 Set으로 변환하여 빠른 조회
        const bossSet = new Set(bosses);

        // active에 있지만 bosses 배열에 없거나 죽은 보스 제거
        for (const boss of this.bossPool.getActiveKeys()) {
            if (!boss.isAlive || !bossSet.has(boss)) {
                this.bossPool.release(boss);
            }
        }

        // 살아있는 보스 업데이트/추가
        for (const boss of bosses) {
            if (boss.isAlive) {
                if (this.bossPool.has(boss)) {
                    this.bossPool.update(boss);
                } else {
                    // 캐시가 준비된 경우 스프라이트 사용
                    if (this.cacheReady) {
                        const sprite = this.bossPool.get(boss);
                        if (sprite) {
                            this.bossPool.update(boss);
                        }
                    }
                }
            }
        }
    }

    clear(): void {
        this.bossPool.clear();
    }

    reset(): void {
        this.bossPool.reset();
    }

    destroy(): void {
        this.bossPool.destroy();
    }
}
